// src/services/api.js
const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

const handleResponse = async (response) => {
  // If unauthorized, clear tokens and force login
  if (response.status === 401) {
    localStorage.removeItem('auth_token');
    localStorage.removeItem('refresh_token');
    throw new Error('Authentication failed');
  }

  let data;
  try {
    data = await response.json();
  } catch (e) {
    data = { detail: response.statusText };
  }
  
  if (!response.ok) {
    let errorMessage = data.detail || data.error || JSON.stringify(data) || 'Something went wrong';
    throw new Error(errorMessage);
  }
  
  return data;
};

const getAuthHeaders = () => {
  const token = localStorage.getItem('auth_token');
  return token ? {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json',
  } : {
    'Content-Type': 'application/json',
  };
};

const getAuthHeadersWithoutContentType = () => {
  const token = localStorage.getItem('auth_token');
  return token ? {
    'Authorization': `Bearer ${token}`,
  } : {};
};

export const api = {
  // Expose BASE_URL for components that need it
  baseURL: BASE_URL,

  login: async (username, password) => {
    try {
      const response = await fetch(`${BASE_URL}/auth/login/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ username, password }),
      });

      const data = await handleResponse(response);
      
      if (data.access) {
        localStorage.setItem('auth_token', data.access);
        if (data.refresh) {
          localStorage.setItem('refresh_token', data.refresh);
        }
        return { success: true };
      }
      return { success: false, error: 'Invalid response from server' };
    } catch (error) {
      console.error('Login error:', error);
      return { 
        success: false, 
        error: error.message || 'Login failed'
      };
    }
  },

  checkAuth: async () => {
    const token = localStorage.getItem('auth_token');
    if (!token) {
      throw new Error('No token found');
    }

    try {
      const response = await fetch(`${BASE_URL}/auth/me/`, {
        method: 'GET',
        headers: getAuthHeaders(),
      });

      return await handleResponse(response);
    } catch (error) {
      console.error('Auth check error:', error);
      throw error;
    }
  },

  refreshToken: async () => {
    const refreshToken = localStorage.getItem('refresh_token');
    if (!refreshToken) {
      throw new Error('No refresh token found');
    }

    try {
      const response = await fetch(`${BASE_URL}/auth/refresh/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ refresh: refreshToken }),
      });

      const data = await handleResponse(response);
      if (data.access) {
        localStorage.setItem('auth_token', data.access);
        return data.access;
      }
      throw new Error('Failed to refresh token');
    } catch (error) {
      console.error('Token refresh error:', error);
      throw error;
    }
  },

  // Vehicle API methods
  getVehicles: async (plateNumber = '') => {
    try {
      const url = plateNumber 
        ? `${BASE_URL}/vehicles/?plate_number=${encodeURIComponent(plateNumber)}`
        : `${BASE_URL}/vehicles/`;
      
      const response = await fetch(url, {
        method: 'GET',
        headers: getAuthHeaders(),
      });
      return await handleResponse(response);
    } catch (error) {
      console.error('Error fetching vehicles:', error);
      throw error;
    }
  },

  getVehicle: async (id) => {
    try {
      const response = await fetch(`${BASE_URL}/vehicles/${id}/`, {
        method: 'GET',
        headers: getAuthHeaders(),
      });
      return await handleResponse(response);
    } catch (error) {
      console.error('Error fetching vehicle:', error);
      throw error;
    }
  },

  addVehicle: async (vehicleData) => {
    try {
      const formData = new FormData();
      
      // Append text fields
      Object.keys(vehicleData).forEach(key => {
        if (key !== 'photo_front_file' && key !== 'photo_back_file' && key !== 'driver_passport_file') {
          if (vehicleData[key] !== null && vehicleData[key] !== undefined && vehicleData[key] !== '') {
            formData.append(key, vehicleData[key]);
          }
        }
      });

      // Append file fields if they exist
      if (vehicleData.photo_front_file) {
        formData.append('photo_front', vehicleData.photo_front_file);
      }
      if (vehicleData.photo_back_file) {
        formData.append('photo_back', vehicleData.photo_back_file);
      }
      if (vehicleData.driver_passport_file) {
        formData.append('driver_passport_photo', vehicleData.driver_passport_file);
      }

      const token = localStorage.getItem('auth_token');
      const url = `${BASE_URL}/vehicles/`;
      

      
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: formData,
      });



      return await handleResponse(response);
    } catch (error) {
      console.error('Error adding vehicle:', error);
      throw error;
    }
  },

  updateVehicle: async (id, vehicleData) => {
    try {
      const formData = new FormData();
      
      // Append text fields
      Object.keys(vehicleData).forEach(key => {
        if (key !== 'photo_front_file' && key !== 'photo_back_file' && key !== 'driver_passport_file' && 
            key !== 'delete_photo_front' && key !== 'delete_photo_back' && key !== 'delete_driver_passport_photo') {
          if (vehicleData[key] !== null && vehicleData[key] !== undefined && vehicleData[key] !== '') {
            formData.append(key, vehicleData[key]);
          }
        }
      });

      // Append file fields if they exist
      if (vehicleData.photo_front_file) {
        formData.append('photo_front', vehicleData.photo_front_file);
      }
      if (vehicleData.photo_back_file) {
        formData.append('photo_back', vehicleData.photo_back_file);
      }
      if (vehicleData.driver_passport_file) {
        formData.append('driver_passport_photo', vehicleData.driver_passport_file);
      }

      // Append deletion flags
      if (vehicleData.delete_photo_front) {
        formData.append('delete_photo_front', 'true');
      }
      if (vehicleData.delete_photo_back) {
        formData.append('delete_photo_back', 'true');
      }
      if (vehicleData.delete_driver_passport_photo) {
        formData.append('delete_driver_passport_photo', 'true');
      }

      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${BASE_URL}/vehicles/${id}/`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: formData,
      });

      return await handleResponse(response);
    } catch (error) {
      console.error('Error updating vehicle:', error);
      throw error;
    }
  },

  deleteVehicle: async (id) => {
    try {

      
      const response = await fetch(`${BASE_URL}/vehicles/${id}/`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });
      

      
      if (response.status === 204 || response.status === 200) {
        return { success: true };
      }
      
      return await handleResponse(response);
    } catch (error) {
      console.error('Error deleting vehicle:', error);
      throw error;
    }
  },

  // Insurance related methods
  updateInsurance: async (vehicleId, formData) => {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${BASE_URL}/vehicles/${vehicleId}/`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: formData,
      });

      
      return await handleResponse(response);
    } catch (error) {
      console.error('Error updating insurance:', error);
      throw error;
    }
  },

  createInsurance: async (data) => {
    try {
      const response = await fetch(`${BASE_URL}/insurances/`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(data),
      });
      
      return await handleResponse(response);
    } catch (error) {
      console.error('Error creating insurance:', error);
      throw error;
    }
  },

  updateInsuranceRecord: async (id, data) => {
    try {

      
      const response = await fetch(`${BASE_URL}/insurances/${id}/`, {
        method: 'PATCH',
        headers: getAuthHeaders(),
        body: JSON.stringify(data),
      });
      
      
      return await handleResponse(response);
    } catch (error) {
      console.error('Error updating insurance record:', error);
      throw error;
    }
  },
  getInsuranceDetails: async (id) => {
    try {
      const response = await fetch(`${BASE_URL}/insurances/${id}/`, {
        method: 'GET',
        headers: getAuthHeaders(),
      });
      
      return await handleResponse(response);
    } catch (error) {
      console.error('Error fetching insurance details:', error);
      throw error;
    }
  },
  getInsurances: async (vehicleId = '', country = '') => {
    try {
      let url = `${BASE_URL}/insurances/`;
      const params = new URLSearchParams();
      
      if (vehicleId) params.append('vehicle_id', vehicleId);
      if (country) params.append('country', country);
      
      if (params.toString()) {
        url += `?${params.toString()}`;
      }
      
      const response = await fetch(url, {
        method: 'GET',
        headers: getAuthHeaders(),
      });
      
      return await handleResponse(response);
    } catch (error) {
      console.error('Error fetching insurances:', error);
      throw error;
    }
  },

  uploadInsuranceDocument: async (insuranceId, file) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await fetch(`${BASE_URL}/insurances/${insuranceId}/upload_document/`, {
        method: 'POST',
        headers: getAuthHeadersWithoutContentType(),
        body: formData,
      });
      
      return await handleResponse(response);
    } catch (error) {
      console.error('Error uploading insurance document:', error);
      throw error;
    }
  },


  deleteInsuranceDocument: async (documentId) => {
    try {
      const response = await fetch(`${BASE_URL}/insurance-documents/${documentId}/`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });
      
      if (response.status === 204 || response.status === 200) {
        return { success: true };
      }
      
      return await handleResponse(response);
    } catch (error) {
      console.error('Error deleting insurance document:', error);
      throw error;
    }
  },

  // Pre-ordered insurance methods
  getPreOrderedInsurances: async (plateNumber = '') => {
    try {
      const url = plateNumber 
        ? `${BASE_URL}/pre-ordered-insurances/?plate_number=${encodeURIComponent(plateNumber)}`
        : `${BASE_URL}/pre-ordered-insurances/`;
      
      const response = await fetch(url, {
        method: 'GET',
        headers: getAuthHeaders(),
      });
      
      return await handleResponse(response);
    } catch (error) {
      console.error('Error fetching pre-ordered insurances:', error);
      throw error;
    }
  },

  createPreOrderedInsurance: async (data) => {
    try {
      const response = await fetch(`${BASE_URL}/pre-ordered-insurances/`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(data),
      });
      
      return await handleResponse(response);
    } catch (error) {
      console.error('Error creating pre-ordered insurance:', error);
      throw error;
    }
  },

  updatePreOrderedInsurance: async (id, data) => {
    try {
      const response = await fetch(`${BASE_URL}/pre-ordered-insurances/${id}/`, {
        method: 'PATCH',
        headers: getAuthHeaders(),
        body: JSON.stringify(data),
      });
      
      return await handleResponse(response);
    } catch (error) {
      console.error('Error updating pre-ordered insurance:', error);
      throw error;
    }
  },

  deletePreOrderedInsurance: async (id) => {
    try {
      const response = await fetch(`${BASE_URL}/pre-ordered-insurances/${id}/`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });
      
      if (response.status === 204 || response.status === 200) {
        return { success: true };
      }
      
      return await handleResponse(response);
    } catch (error) {
      console.error('Error deleting pre-ordered insurance:', error);
      throw error;
    }
  },

  getFleetSummary: async () => {
    try {
      const response = await fetch(`${BASE_URL}/vehicles/fleet_summary/`, {
        method: 'GET',
        headers: getAuthHeaders(),
      });
      return await handleResponse(response);
    } catch (error) {
      console.error('Error fetching fleet summary:', error);
      throw error;
    }
  },

  searchVehicles: async (query) => {
    try {
      const response = await fetch(`${BASE_URL}/vehicles/search/?q=${encodeURIComponent(query)}`, {
        method: 'GET',
        headers: getAuthHeaders(),
      });
      return await handleResponse(response);
    } catch (error) {
      console.error('Error searching vehicles:', error);
      throw error;
    }
  }
};

let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

export const setupInterceptors = () => {
  const originalFetch = window.fetch;
  
  window.fetch = async (...args) => {
    const [url, config = {}] = args;
    
    if (url.includes('/auth/refresh/')) {
      return originalFetch(url, config);
    }
    
    try {
      const response = await originalFetch(url, config);
      
      if (response.status !== 401) {
        return response;
      }
      
      const refreshToken = localStorage.getItem('refresh_token');
      if (!refreshToken) {
        localStorage.removeItem('auth_token');
        window.location.href = '/login';
        return response;
      }
      
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then(token => {
          const newConfig = {
            ...config,
            headers: {
              ...config.headers,
              'Authorization': `Bearer ${token}`,
            }
          };
          return originalFetch(url, newConfig);
        });
      }
      
      isRefreshing = true;
      
      try {
        await api.refreshToken();
        const newToken = localStorage.getItem('auth_token');
        
        isRefreshing = false;
        processQueue(null, newToken);
        
        const newConfig = {
          ...config,
          headers: {
            ...config.headers,
            'Authorization': `Bearer ${newToken}`,
          }
        };
        
        return await originalFetch(url, newConfig);
      } catch (refreshError) {
        isRefreshing = false;
        processQueue(refreshError, null);
        
        localStorage.removeItem('auth_token');
        localStorage.removeItem('refresh_token');
        window.location.href = '/login';
        
        return response;
      }
    } catch (error) {
      throw error;
    }
  };
};